import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import { RootState } from "../../state/store";
import CountryCodeDropDownAndPhoneField from "./countrycodedropdownandphonefield.tsx";
import Swal from "sweetalert2";

type passwordModalProps = {
    show: boolean,
    onClose: () => void,
    baseUrl: string,
    currentUser: any,
}

export function PasswordModal({ show, onClose, baseUrl, currentUser }: passwordModalProps) {

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate(); // <-- add this
    const client = useSelector((state: RootState) => state.client.client);
    const client_uid = client?.uid || "clnt_QI9Jd1m0"; // fallback to default if not available
    const default_configs = client ? JSON.parse(client.default_configs) : {};


    // ESC key to close
    useEffect(() => {
        const handleEsc = (e: any) => {
            if (e.key === "Escape") onClose();
        };
        if (show) window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [show, onClose]);



    const handleLogin = async (e: any) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        let newErrors: { role?: string, password?: string } = {};

        if (!password) {
            newErrors.password = "Password is required";
        }

        const checks = getPasswordChecks(password);
        const isValidPassword = Object.values(checks).every(Boolean);

        if (!isValidPassword) {
            newErrors.password = "Invalid password format";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // setIsPasswordValid(validatePassword(password));
            return;
        }

        if (!error) {
            try {
                const formData = new FormData();
                formData.append("password", password);
                formData.append("client_uid", client_uid);
                formData.append("token", currentUser.token);

                let response = await axios.post(baseUrl + '/updateSupportOrAdminPassword', formData);

                if (response.data.status === "OK") {
                    Swal.fire({
                        icon: "success",
                        title: "Passord oppdatert",
                        text: "Du blir videresendt...",
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    // ✅ Auto close modal
                    setTimeout(() => {
                        onClose();

                        // ✅ Redirect based on role
                        if (currentUser?.role === "support" || currentUser?.role === "admin") {
                            navigate("/support_admin/dashboard");
                        } else {
                            navigate("/");
                        }
                    }, 1500);
                } else {
                    setError(response.data.message || "Noe gikk galt");
                }
            } catch (err: any) {
                console.error(err);
                setError(err.response?.data?.message || "Serverfeil");
            } finally {
                setLoading(false);
            }
        }

    };

    const titleMap = {
        login: `Oppsett passord ${currentUser.name}`,
    };

    const handleConfirmPasswordChanged = (cPassword: string) => {
        setConfirmPassword(cPassword);

        // Don't validate yet → wait for blur
        if (!confirmTouched) return;

        if (password !== cPassword) {
            setError("Passord stemmer ikke overens");
        } else {
            setError("");
        }
    };

    const handleConfirmBlur = () => {
        setConfirmTouched(true);

        if (!confirmPassword) return;

        if (password !== confirmPassword) {
            setError("Passord stemmer ikke overens");
        } else {
            setError("");
        }
    };

    const validatePassword = (value: string) => {
        const regex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

        return regex.test(value);
    };

    const [isPasswordValid, setIsPasswordValid] = useState(true);

    const getPasswordChecks = (value: string) => {
        return {
            length: value.length >= 8,
            upper: /[A-Z]/.test(value),
            lower: /[a-z]/.test(value),
            number: /\d/.test(value),
            special: /[^A-Za-z\d]/.test(value),
        };
    };
    const [passwordChecks, setPasswordChecks] = useState({
        length: false,
        upper: false,
        lower: false,
        number: false,
        special: false,
    });
    const [errors, setErrors] = useState<{ role?: string, password?: string }>({});

    const [showPassword, setShowPassword] = useState(false);

    const generateStrongPassword = () => {
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const special = "!@#$%^&*()_+[]{}|;:,.<>?";

        const all = upper + lower + numbers + special;

        let password = "";

        // ensure at least one of each
        password += upper[Math.floor(Math.random() * upper.length)];
        password += lower[Math.floor(Math.random() * lower.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += special[Math.floor(Math.random() * special.length)];

        // fill remaining length
        for (let i = 4; i < 12; i++) {
            password += all[Math.floor(Math.random() * all.length)];
        }

        // shuffle
        password = password
            .split("")
            .sort(() => Math.random() - 0.5)
            .join("");

        return password;
    };

    const [confirmTouched, setConfirmTouched] = useState(false);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
                    >
                        <button
                            onClick={onClose}
                            aria-label="Lukk"
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{titleMap['login']}</h2>

                        <form className="space-y-5" onSubmit={handleLogin}>

                            {/* Identifier for Password */}


                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Passord
                                </label>

                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="********"
                                        value={password}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPassword(value);

                                            const checks = getPasswordChecks(value);
                                            setPasswordChecks(checks);

                                            const isValid = Object.values(checks).every(Boolean);
                                            setIsPasswordValid(isValid);

                                            setErrors(prev => ({
                                                ...prev,
                                                password: isValid ? "" : "Invalid password format"
                                            }));
                                        }}
                                        className={`w-full rounded-2xl border px-4 py-3 text-sm pr-24 ${!isPasswordValid
                                            ? "border-red-500"
                                            : "border-slate-200"
                                            }`}
                                    />

                                    {/* Show/Hide Button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700"
                                    >
                                        {showPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                            </div>
                            {/* Action buttons */}
                            <div className="flex items-center justify-between mt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newPass = generateStrongPassword();
                                        setPassword(newPass);

                                        const checks = getPasswordChecks(newPass);
                                        setPasswordChecks(checks);
                                        setIsPasswordValid(true);

                                        setErrors(prev => ({ ...prev, password: "" }));
                                    }}
                                    className="bg-[#ff5b24] py-2 px-2 rounded-full text-xs text-white font-medium hover:underline"
                                >
                                    Generate PW
                                </button>
                            </div>

                            {/* Checklist */}
                            {password && (
                                <div className="mt-2 space-y-1">
                                    {[
                                        { key: "length", label: "At least 8 characters" },
                                        { key: "upper", label: "One uppercase letter" },
                                        { key: "lower", label: "One lowercase letter" },
                                        { key: "number", label: "One number" },
                                        { key: "special", label: "One special character" },
                                    ].map((item) => {
                                        const valid = passwordChecks[item.key];

                                        return (
                                            <div key={item.key} className="flex items-center gap-2 text-xs">
                                                <span
                                                    className={`h-4 w-4 flex items-center justify-center rounded-full text-[10px] ${valid
                                                        ? "bg-green-500 text-white"
                                                        : "bg-slate-200 text-slate-500"
                                                        }`}
                                                >
                                                    {valid ? "✓" : ""}
                                                </span>
                                                <span className={valid ? "text-green-600" : "text-slate-500"}>
                                                    {item.label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bekrefte Passord</label>
                                <input
                                    value={confirmPassword}
                                    onChange={(e) => handleConfirmPasswordChanged(e.target.value)}
                                    onBlur={handleConfirmBlur}
                                    type="password"
                                    placeholder="••••••••"
                                    className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm"
                                    required
                                    autoComplete="current-password"
                                />

                                {confirmTouched && confirmPassword && (
                                    password === confirmPassword ? (
                                        <p className="text-green-600 text-sm mt-1">
                                            ✅ Passord stemmer
                                        </p>
                                    ) : (
                                        <p className="text-red-500 text-sm mt-1">
                                            ❌ Passord stemmer ikke
                                        </p>
                                    )
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !!error || !password || !confirmPassword}
                                className={`w-full font-semibold py-2 rounded-full shadow-md transition-all duration-150 
                                ${loading || error ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                            >
                                {loading ? 'Oppdaterer...' : 'Oppdater'}
                            </button>

                        </form>
                    </motion.div>
                </motion.div>
            )
            }
        </AnimatePresence >
    );
}
